import { Content } from "@iventis/translations";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { EventBlockTypeDataFields } from "@iventis/domain-model/model/eventBlockTypeDataFields";
import { AdditionalDataFieldProperties } from "@iventis/types";
import { v4 as uuid } from "uuid";
import { EMPTY_GUID } from "@iventis/utilities";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";

export const getEventBlockTitle = (index: number, translate: (key: string) => string): string => {
    switch (index) {
        case 0:
            return translate(Content.settings.scheduleConfig.eventBlockTitleTop);
        case 1:
            return translate(Content.settings.scheduleConfig.eventBlockTitleSecond);
        case 2:
            return translate(Content.settings.scheduleConfig.eventBlockTitleThird);
        default:
            return `${index} ${translate(Content.settings.scheduleConfig.eventBlockTitleDefault)}`;
    }
};

export const getSummaryDataFieldValues = (calendarLevel: number, eventBlockTypes: ScheduleConfigEventBlockType[], dataFields: DataField[]) => {
    if (calendarLevel === 1) {
        const bottomLevelEventBlock = eventBlockTypes.find(({ calendarLevel }) => calendarLevel === 2);
        const bottomLevelEventBlockDataFieldIds = bottomLevelEventBlock.dataFields.map(({ dataFieldId }) => dataFieldId);
        return dataFields.filter(({ id }) => bottomLevelEventBlockDataFieldIds.includes(id));
    }
    return undefined;
};

export const orderEventBlockConfigs = (configs: ScheduleConfigEventBlockType[]): ScheduleConfigEventBlockType[] => {
    const calendarOnlyConfigs = configs.filter(({ calendarLevel }) => calendarLevel !== null);
    return calendarOnlyConfigs.sort((a, b) => a.calendarLevel - b.calendarLevel);
};

/** Updates a list of datafield by removing deleted dataFields and updating changed ones */
export const updateScheduleDataFields = (updatedEventBlockDataFields: DataField[], previousEventBlockDataFields: DataField[], allDataFields: DataField[]) => {
    const eventBlockDataFieldsIds = [...updatedEventBlockDataFields.map(({ id }) => id), ...previousEventBlockDataFields.map(({ id }) => id)];
    return [...allDataFields.filter((df) => !eventBlockDataFieldsIds.includes(df.id)), ...updatedEventBlockDataFields];
};

export const convertDataFieldToEventBlockTypeDataField = (dataField: AdditionalDataFieldProperties, index: number): EventBlockTypeDataFields => ({
    dataFieldId: dataField.id,
    index,
    required: dataField.required,
    namingOrder: dataField.namingOrder,
    systemDataFieldType: dataField.systemDataField,
});

export const createDefaultEventBlockTypes = (
    codeDataFieldId: string,
    styleDataFieldId: string,
    topLevelVenueLocationTypeId: string,
    secondLevelVenueLocationTypeId
): ScheduleConfigEventBlockType[] => {
    const topLevelEventBlockId = uuid();

    return [
        createDefaultEventBlockType({
            id: topLevelEventBlockId,
            calendarLevel: 1,
            allowedParentType: EMPTY_GUID,
            dataFields: [
                { dataFieldId: codeDataFieldId, index: 1, namingOrder: 1, required: true, systemDataFieldType: EventBlockSystemDataFieldType.Code },
                { dataFieldId: styleDataFieldId, index: 2, required: true, systemDataFieldType: EventBlockSystemDataFieldType.Style },
            ],
            name: "Session",
            allowedVenueTypes: [topLevelVenueLocationTypeId].filter((x) => x),
        }),
        createDefaultEventBlockType({
            calendarLevel: 2,
            allowedParentType: topLevelEventBlockId,
            name: "Schedule Item",
            allowedVenueTypes: [secondLevelVenueLocationTypeId],
        }),
        createDefaultEventBlockType({
            calendarLevel: null,
            name: "Activity",
            allowedVenueTypes: [topLevelVenueLocationTypeId, secondLevelVenueLocationTypeId].filter((x) => x),
            dcasCreationAllowed: true,
        }),
    ];
};

export const createEventBlockCodeDataField = (): DataField => ({
    id: uuid(),
    name: "Code",
    customerIdentifierName: undefined,
    allowedMetadataProperties: [],
    type: DataFieldType.Text,
    listValues: [],
    projectId: undefined,
    hierarchyMetadataPropertyName: undefined,
    showInApi: false,
    apiNameSingular: undefined,
    apiNamePlural: undefined,
    listItemProperties: [],
    listItemRelationships: [],
    order: 0,
    deprecated: false,
    defaultValue: null,
});

export const createEventBlockStyleDataField = (): DataField => ({
    id: uuid(),
    name: "Style",
    customerIdentifierName: undefined,
    allowedMetadataProperties: ["colour"],
    type: DataFieldType.List,
    listValues: [],
    projectId: undefined,
    hierarchyMetadataPropertyName: undefined,
    showInApi: false,
    apiNameSingular: undefined,
    apiNamePlural: undefined,
    listItemProperties: [],
    listItemRelationships: [],
    order: 0,
    deprecated: false,
    defaultValue: null,
});

export const createDefaultEventBlockType = (eventBlock: Partial<ScheduleConfigEventBlockType> = {}) => ({
    id: uuid(),
    name: "",
    apiNameSingular: "",
    apiNamePlural: "",
    calendarLevel: undefined,
    allowedParentType: EMPTY_GUID,
    dcasCreationAllowed: false,
    childSummaryDataFieldIds: [],
    allowedRelativeTypes: [],
    allowedVenueTypes: [],
    customerIdDataFieldId: undefined,
    dataFields: [],
    ...eventBlock,
});
